import React from 'react';
import DOMPurify from 'dompurify';

interface SafeHtmlProps {
  html: string | null | undefined;
  className?: string;
  tag?: React.ElementType;
  allowedTags?: string[];
  allowedAttributes?: string[];
}

/**
 * SafeHtml component - Safely renders HTML content using DOMPurify sanitization
 *
 * This component replaces dangerouslySetInnerHTML with a safer alternative that
 * sanitizes HTML to prevent XSS attacks while preserving safe formatting.
 *
 * @param html - The HTML string to render (can be null/undefined)
 * @param className - Optional CSS class name
 * @param tag - HTML tag to use as wrapper (default: 'div')
 * @param allowedTags - Optional array of allowed HTML tags (default: common safe tags)
 * @param allowedAttributes - Optional array of allowed HTML attributes (default: common safe attributes)
 */
const SafeHtml: React.FC<SafeHtmlProps> = ({ html, className, tag: Tag = 'div', allowedTags, allowedAttributes }) => {
  // Return null if no HTML content
  if (!html || typeof html !== 'string') {
    return null;
  }

  // Default allowed tags for common formatting needs
  const defaultAllowedTags = [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    'span',
    'div',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'a',
    'table',
    'thead',
    'tbody',
    'tr',
    'td',
    'th',
    'blockquote',
    'pre',
    'code',
  ];

  // Default allowed attributes
  const defaultAllowedAttributes = ['href', 'target', 'rel', 'class', 'id', 'style', 'colspan', 'rowspan'];

  // Sanitize HTML using DOMPurify
  const sanitizedHtml = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: allowedTags || defaultAllowedTags,
    ALLOWED_ATTR: allowedAttributes || defaultAllowedAttributes,
    ALLOW_DATA_ATTR: false,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false,
  });

  return <Tag className={className} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
};

export default SafeHtml;
